import { useEffect, useRef, useState } from "react";

/**
 * <ArtBackdrop>
 *
 * Fond artistique très subtil pour habiller une page premium. L'œuvre est
 * affichée en cover, très floutée et désaturée, avec un dégradé sombre qui
 * garantit la lisibilité du contenu superposé.
 *
 * Usage :
 *   <ArtBackdrop src="/art/sorola/promenade-1.jpg" opacity={0.18} />
 */
export default function ArtBackdrop({
  src,
  opacity = 0.18,
  blur = 18,
  saturate = 0.85,
  parallax = false,
  overlay = "linear-gradient(180deg, rgba(11,58,42,0.55) 0%, rgba(11,58,42,0.78) 100%)",
}) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!parallax) return undefined;
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      // léger parallax vertical (~0.15)
      setOffset(rect.top * 0.12);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [parallax]);

  return (
    <div
      ref={ref}
      aria-hidden
      className="absolute inset-0 overflow-hidden pointer-events-none"
      data-testid="art-backdrop"
    >
      <div
        className="absolute inset-0 will-change-transform"
        style={{
          backgroundImage: `url(${src})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: `blur(${blur}px) saturate(${saturate})`,
          opacity,
          transform: parallax ? `translate3d(0, ${offset}px, 0) scale(1.15)` : "scale(1.15)",
          transition: "opacity 1.2s ease",
        }}
      />
      <div className="absolute inset-0" style={{ background: overlay }} />
    </div>
  );
}
